import { useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { EVENTS, trackEvent } from '../../../../../events'
import Sheetlet from '../../Sheetlet/Sheetlet'
import { renderToString } from 'react-dom/server'
import { ServerContext } from '../../SheetState'
import { Server } from '../../../../../api/Server'
import { EmptyCalculatedColumns } from '../EmptyCalculatedColumns/EmptyCalculatedColumns'
import googleSheetsIcon from '../../../../../assets/source/GoogleSheets.svg'
import { useCalculatedFields } from '../../../../../api/hooks/calculatedField/useCalculatedFields'
import { TrashIcon, PlusIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopDialog,
    ScoopMenu,
    ScoopMenuItem,
    ScoopSelector,
    ScoopTextField,
    ScoopText,
    ScoopSwitch,
    ScoopSkeleton,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
} from '../../../../ui'
import { useModal, MODALS } from '../../../../modals'
import { useWorkspaceMetadata } from '../../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { cloneDeep } from 'lodash'
import styles from './CalculatedColumns.module.scss'

export const CalculatedColumns = ({ inboxID, sourceDetails }) => {
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const token = useSelector((state) => state.auth.token)
    const { workspaceMetadata, refetch: refetchMetadata } = useWorkspaceMetadata()

    const deleteModal = useModal(MODALS.COMMON.DELETE)

    const [selectedTargetInboxID, setSelectedTargetInboxID] = useState('')
    const [selectedTargetInbox, setSelectedTargetInbox] = useState()
    const [targetFieldList, setTargetFieldList] = useState([])
    const [sourceColumn, setSourceColumn] = useState('')
    const [targetColumn, setTargetColumn] = useState('')
    const [selectedModel, setSelectedModel] = useState('')
    const [targetValueColumn, setTargetValueColumn] = useState('')
    const [previewData, setPreviewData] = useState(null)
    const [initialized, setInitialized] = useState(false)
    const [loading, setLoading] = useState(false)
    const [currentServerContext, setCurrentServerContext] = useState(null)
    const [worksheetID, setWorksheetID] = useState(null)
    const [sheetRange, setSheetRange] = useState({
        sheetType: 'CalculatedField',
        advanced: false,
        inboxID: inboxID,
    })
    const [editMode, setEditMode] = useState(false)
    const [openAddFormula, setOpenAddFormula] = useState(false)
    const [openAddFormulaFromBlank, setOpenAddFormulaFromBlank] = useState(false)
    const [openAddLookup, setOpenAddLookup] = useState(false)
    const [openAddSegment, setOpenAddSegment] = useState(false)
    const [prompt, setPrompt] = useState('')
    const [formula, setFormula] = useState('')
    const [formulaName, setFormulaName] = useState('')
    const {
        data: calculatedFieldsData,
        isLoading: calculatedFieldsLoading,
        refetch,
    } = useCalculatedFields(inboxID)
    const [anchorEl, setAnchorEl] = useState(null)
    const [previewResult, setPreviewResult] = useState(null)
    const [aiSuggest, setAISuggest] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)
    const [valid, setValid] = useState(false)

    useEffect(() => {
        const css = new ServerContext(new Server(workspaceID, userID, token))
        setCurrentServerContext(css)
    }, [workspaceID, userID, token])

    useEffect(() => {
        if (!initialized && currentServerContext?.server) {
            sheetRange.inboxID = inboxID
            setSheetRange({ ...sheetRange })
            setLoading(true)
            setInitialized(true)
            let newFieldList = []
            for (let i = 0; i < sourceDetails.tables[0].columns.length; i++) {
                let colName = sourceDetails.tables[0].columns[i].columnName
                newFieldList.push({ id: colName, value: colName })
            }
            setTargetFieldList(newFieldList)
        }
    }, [inboxID, initialized, currentServerContext, sheetRange])

    const handleSave = () => {
        try {
            currentServerContext.server.sheetPostData(
                {
                    action: 'flushWorksheetCache',
                    sheetRange: {
                        inboxID: inboxID,
                    },
                },
                () => {}
            )
        } catch (e) {
            console.error(e)
        }
        setPreviewData(null)
        setInitialized(false)
    }

    const handleDelete = (formula) => {
        currentServerContext.server.sheetPostData(
            {
                action: 'deleteCalculatedField',
                sheetRange: {
                    inboxID: inboxID,
                },
                columnName: formula.columnName,
            },
            () => {
                refetch()
                refetchMetadata()
            }
        )
    }

    const handleConfirmDelete = async (formula) => {
        await deleteModal.show({
            title: 'Formula',
            description: `Are you sure you want to delete the formula "${formula.columnName}"?`,
            onDelete: () => handleDelete(formula),
        })
    }

    function reset() {
        setFormula('')
        setValid(false)
        setPrompt('')
        setPreviewResult(null)
    }

    const createCalculatedField = (fname, fvalue) => {
        try {
            trackEvent(EVENTS.DATASET.CREATE_CALCULATED_COLUMN, { formulaName: fname })
            currentServerContext.server.sheetPostData(
                {
                    action: 'addCalculatedField',
                    sheetRange: {
                        inboxID: inboxID,
                    },
                    formulaName: fname,
                    formula: fvalue,
                },
                (result) => {
                    if (result.error) {
                        setErrorMessage(result.error)
                    } else {
                        refetchMetadata()
                        refetch()
                        reset()
                    }
                    setOpenAddFormulaFromBlank(false)
                    setOpenAddSegment(false)
                    setOpenAddLookup(false)
                }
            )
        } catch (e) {
            console.error(e)
        }
    }

    const handleAISuggest = () => {
        try {
            currentServerContext.server.sheetPostData(
                {
                    action: 'generateAIFormula',
                    prompt: prompt,
                    sheetRange: {
                        inboxID: inboxID,
                    },
                },
                (result) => {
                    setAISuggest(false)
                    if (result.formula) {
                        setFormula(formula + result.formula)
                    }
                }
            )
        } catch (e) {
            console.error(e)
        }
    }

    const handlePreviewFormula = (formula) => {
        try {
            setPreviewResult(null)
            currentServerContext.server.sheetPostData(
                {
                    action: 'formulaPreview',
                    formula: formula,
                    sheetRange: {
                        inboxID: inboxID,
                    },
                },
                (result) => {
                    if (result.error) {
                        setErrorMessage(result.error)
                    } else {
                        setPreviewResult(result)
                        setErrorMessage(null)
                        setValid(true)
                    }
                }
            )
        } catch (e) {
            setErrorMessage('Unable to validate formula')
            console.error(e)
        }
    }

    function setTargetInboxID(inboxID) {
        setSelectedTargetInboxID(inboxID)
        let newInbox = workspaceMetadata?.inboxes.find((x) => x.inboxID === inboxID)
        setSelectedTargetInbox(newInbox)
        let newFieldList = []
        for (let i = 0; i < newInbox.tables[0].columns.length; i++) {
            let colName = newInbox.tables[0].columns[i].columnName
            newFieldList.push({ id: colName, value: colName })
        }
        setTargetFieldList(newFieldList)
        setValid(false)
    }

    const testValid = (value) => {
        if (workspaceMetadata) {
            const inbox = workspaceMetadata.inboxes.find((x) => x.inboxID === inboxID)
            const valid = !inbox.tables.some((table) =>
                table.columns.some((col) => col.columnName === value)
            )
            setValid(valid)
        }
    }

    const getReversedCalculatedFields = () => {
        const temp = cloneDeep(calculatedFieldsData)
        temp.reverse()
        return temp
    }

    return (
        <ScoopColumn className={`${styles.mainContainer} ${styles.calculatedColumnsScope}`}>
            {!previewData && !loading ? (
                <EmptyCalculatedColumns inboxID={inboxID} />
            ) : (
                currentServerContext && (
                    <ScoopColumn className={styles.contentContainer}>
                        <ScoopRow className={styles.headerRow}>
                            <ScoopRow className={styles.worksheetEditSection}>
                                <ScoopText className={styles.worksheetEditLabel}>
                                    Worksheet Edit
                                </ScoopText>
                                <ScoopSwitch
                                    checked={editMode}
                                    onChange={() => {
                                        setEditMode(!editMode)
                                    }}
                                />
                            </ScoopRow>
                            {!editMode && (
                                <ScoopRow className={styles.addButtonContainer}>
                                    <ScoopButton
                                        leftSection={
                                            <PlusIcon size={16} className={styles.iconWhite} />
                                        }
                                        onClick={(event) => {
                                            setAnchorEl(event.currentTarget)
                                            setOpenAddFormula(true)
                                        }}
                                    >
                                        Add Formula
                                    </ScoopButton>
                                </ScoopRow>
                            )}
                        </ScoopRow>

                        {calculatedFieldsLoading ? (
                            <ScoopCenter className={styles.loadingSkeletonContainer}>
                                {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                                    <ScoopSkeleton
                                        key={item}
                                        variant="rectangular"
                                        width="100%"
                                        height={34}
                                    />
                                ))}
                            </ScoopCenter>
                        ) : !editMode ? (
                            calculatedFieldsData && calculatedFieldsData.length > 0 ? (
                                <ScoopColumn>
                                    <div className={styles.formulasSection}>
                                        <ScoopText className={styles.sectionTitle}>
                                            Formulas
                                        </ScoopText>
                                        <div className={styles.formulasGrid}>
                                            {getReversedCalculatedFields().map(
                                                (calculatedField) =>
                                                    !calculatedField.complex && (
                                                        <div
                                                            key={calculatedField.columnName}
                                                            className={styles.formulaGridItem}
                                                        >
                                                            <ScoopRow
                                                                className={styles.formulaItem}
                                                            >
                                                                <ScoopColumn
                                                                    className={
                                                                        styles.formulaContent
                                                                    }
                                                                >
                                                                    <ScoopText
                                                                        className={
                                                                            styles.formulaName
                                                                        }
                                                                    >
                                                                        {calculatedField.columnName}
                                                                    </ScoopText>
                                                                    <ScoopText
                                                                        className={
                                                                            styles.formulaText
                                                                        }
                                                                    >
                                                                        {calculatedField.complex
                                                                            ? 'Complex Formula'
                                                                            : calculatedField.formula}
                                                                    </ScoopText>
                                                                </ScoopColumn>
                                                                <ScoopButton
                                                                    variant="text"
                                                                    onClick={() =>
                                                                        handleConfirmDelete(
                                                                            calculatedField
                                                                        )
                                                                    }
                                                                    className={styles.deleteButton}
                                                                >
                                                                    <TrashIcon
                                                                        size={20}
                                                                        className={styles.iconRed}
                                                                    />
                                                                </ScoopButton>
                                                            </ScoopRow>
                                                        </div>
                                                    )
                                            )}
                                        </div>
                                    </div>
                                    <ScoopText className={styles.complexFormulasTitle}>
                                        Complex Formulas Referring to Worksheet Content (use
                                        Worksheet edit)
                                    </ScoopText>
                                    <div className={styles.lookupSection}>
                                        {calculatedFieldsData.map(
                                            (calculatedField) =>
                                                calculatedField.complex && (
                                                    <ScoopText key={calculatedField.columnName}>
                                                        {calculatedField.columnName}
                                                    </ScoopText>
                                                )
                                        )}
                                    </div>
                                </ScoopColumn>
                            ) : (
                                <ScoopCenter className={styles.noFormulasContainer}>
                                    <ScoopText className={styles.noFormulasText}>
                                        No formulas found
                                    </ScoopText>
                                </ScoopCenter>
                            )
                        ) : (
                            <ScoopColumn className={styles.editModeContainer}>
                                <ScoopRow className={styles.editModeHeader}>
                                    <ScoopText className={styles.editModeDescription}>
                                        Create and edit custom calculated columns that can be added
                                        to your raw report source data. You can also choose to not
                                        include columns from your raw reports.
                                    </ScoopText>
                                    <ScoopRow className={styles.editModeActions}>
                                        <ScoopRow className={styles.advancedToggle}>
                                            <ScoopText className={styles.advancedLabel}>
                                                Advanced
                                            </ScoopText>
                                            <ScoopSwitch
                                                checked={sheetRange.advanced}
                                                onChange={() => {
                                                    sheetRange.advanced = !sheetRange.advanced
                                                    setSheetRange({
                                                        ...sheetRange,
                                                    })
                                                }}
                                            />
                                        </ScoopRow>
                                        <ScoopColumn className={styles.saveButtonContainer}>
                                            <ScoopButton onClick={handleSave}>
                                                Save Changes
                                            </ScoopButton>
                                        </ScoopColumn>
                                    </ScoopRow>
                                </ScoopRow>
                                <Sheetlet
                                    controlledHeight={false}
                                    setWorksheetID={setWorksheetID}
                                    serverContext={currentServerContext}
                                    sheetRange={sheetRange}
                                    locked={!sheetRange.advanced}
                                    embeddedSizeProps={{ height: 'auto' }}
                                    cornerHTML={
                                        worksheetID
                                            ? `<a href="https://docs.google.com/spreadsheets/d/${worksheetID}" target="_blank">${renderToString(
                                                  <img
                                                      alt={'Google sheets'}
                                                      src={googleSheetsIcon}
                                                  />
                                              )}</a>`
                                            : ''
                                    }
                                />
                            </ScoopColumn>
                        )}
                    </ScoopColumn>
                )
            )}
            <ScoopMenu
                open={openAddFormula}
                anchorEl={anchorEl}
                onClose={() => {
                    setOpenAddFormula(false)
                    setAnchorEl(null)
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <ScoopMenuItem
                    framedIcon
                    leftSection={<PlusIcon size={16} className={styles.iconRed} />}
                    primary="Create a new formula"
                    onClick={() => {
                        setOpenAddFormulaFromBlank(true)
                        setOpenAddFormula(false)
                    }}
                />
                <ScoopMenuItem
                    framedIcon
                    leftSection={<MagnifyingGlassIcon size={16} className={styles.iconPink} />}
                    primary="Create a lookup formula"
                    onClick={() => {
                        setOpenAddLookup(true)
                        setOpenAddFormula(false)
                    }}
                />
                <ScoopMenuItem
                    framedIcon
                    leftSection={<MagnifyingGlassIcon size={16} className={styles.iconPink} />}
                    primary="Apply saved segment/cluster model"
                    onClick={() => {
                        setOpenAddSegment(true)
                        setOpenAddFormula(false)
                    }}
                />
            </ScoopMenu>
            {openAddFormulaFromBlank && (
                <ScoopDialog
                    className={styles.dialogMedium}
                    open={openAddFormulaFromBlank}
                    onClose={() => {
                        setOpenAddFormulaFromBlank(false)
                    }}
                    title={'Create a new formula'}
                    actions={
                        <ScoopRow className={styles.dialogActions}>
                            <ScoopButton
                                variant="grey"
                                onClick={() => {
                                    setOpenAddFormulaFromBlank(false)
                                    reset()
                                }}
                            >
                                Cancel
                            </ScoopButton>
                            <ScoopButton
                                onClick={() => {
                                    createCalculatedField(formulaName, formula)
                                }}
                                disabled={!valid}
                            >
                                Create
                            </ScoopButton>
                        </ScoopRow>
                    }
                >
                    <ScoopColumn className={styles.dialogContent}>
                        <ScoopColumn>
                            <ScoopText className={styles.formLabel}>Formula name</ScoopText>
                            <ScoopTextField
                                value={formulaName}
                                onChange={(e) => {
                                    setFormulaName(e.target.value)
                                    testValid(e.target.value)
                                }}
                                placeholder="Enter formula name"
                            />
                        </ScoopColumn>
                        {!aiSuggest && (
                            <ScoopColumn>
                                <ScoopText className={styles.formLabel}>
                                    Type your formula here
                                </ScoopText>
                                <ScoopTextField
                                    value={formula}
                                    onChange={(e) => setFormula(e.target.value)}
                                    multiline
                                    rows={2}
                                    placeholder={'Start typing your formula here...'}
                                />
                                {errorMessage && (
                                    <ScoopText className={styles.errorMessage}>
                                        Error: {errorMessage}
                                    </ScoopText>
                                )}
                            </ScoopColumn>
                        )}
                        {aiSuggest && (
                            <ScoopDialog
                                className={styles.dialogMedium}
                                open={true}
                                onClose={() => {
                                    setAISuggest(false)
                                }}
                                title={'Generate formula with AI'}
                                actions={
                                    <ScoopRow className={styles.dialogActions}>
                                        <ScoopButton
                                            variant="grey"
                                            onClick={() => {
                                                setAISuggest(false)
                                            }}
                                        >
                                            Cancel
                                        </ScoopButton>
                                        <ScoopButton
                                            onClick={() => {
                                                handleAISuggest(prompt)
                                            }}
                                        >
                                            Generate
                                        </ScoopButton>
                                    </ScoopRow>
                                }
                            >
                                <ScoopTextField
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    multiline
                                    rows={4}
                                    placeholder={'Start typing your prompt here...'}
                                />
                            </ScoopDialog>
                        )}
                        {!aiSuggest && (
                            <ScoopRow>
                                <ScoopButton
                                    variant="grey"
                                    className={styles.buttonWithMargin}
                                    onClick={() => setAISuggest(true)}
                                >
                                    Generate with AI
                                </ScoopButton>
                                {formula && formula.length > 0 && (
                                    <ScoopButton
                                        variant={valid ? 'grey' : 'default'}
                                        onClick={() => handlePreviewFormula(formula)}
                                    >
                                        Preview/Test Formula
                                    </ScoopButton>
                                )}
                                {previewResult && previewResult.result && (
                                    <div className={styles.previewContainer}>
                                        <div className={styles.previewLabel}>Preview</div>
                                        <div className={styles.previewResult}>
                                            {Array.isArray(previewResult.result) &&
                                                previewResult.result.map((val, idx) => (
                                                    <div key={idx}>{val}</div>
                                                ))}
                                            {!Array.isArray(previewResult.result) && (
                                                <div>{previewResult.result}</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </ScoopRow>
                        )}
                    </ScoopColumn>
                </ScoopDialog>
            )}
            {openAddLookup && (
                <ScoopDialog
                    className={styles.dialogLarge}
                    open={openAddLookup}
                    onClose={() => {
                        setOpenAddLookup(false)
                    }}
                    title={'Add a column that looks up a value in another dataset'}
                    actions={
                        <ScoopRow className={styles.dialogActions}>
                            <ScoopButton
                                variant="grey"
                                onClick={() => {
                                    setOpenAddLookup(false)
                                }}
                            >
                                Cancel
                            </ScoopButton>
                            <ScoopButton
                                onClick={() => {
                                    createCalculatedField(
                                        formulaName,
                                        "ScoopLookup('" +
                                            sourceColumn +
                                            '\',"' +
                                            selectedTargetInbox.label +
                                            '","' +
                                            targetColumn +
                                            '","' +
                                            targetValueColumn +
                                            '")'
                                    )
                                }}
                                disabled={!valid}
                            >
                                Create
                            </ScoopButton>
                        </ScoopRow>
                    }
                >
                    <ScoopColumn className={styles.dialogContent}>
                        <ScoopColumn>
                            <ScoopText className={styles.formLabel}>
                                Name of new column to create
                            </ScoopText>
                            <ScoopTextField
                                value={formulaName}
                                onChange={(e) => {
                                    setFormulaName(e.target.value)
                                    testValid(e.target.value)
                                }}
                                placeholder="Enter new column name"
                            />
                        </ScoopColumn>
                        <ScoopColumn>
                            <ScoopSelector
                                label="Select the column that you would like to lookup"
                                value={sourceColumn}
                                onChange={(e) => setSourceColumn(e.target.value)}
                                options={
                                    sourceDetails?.tables[0].columns.map((col) => ({
                                        label: col.columnName,
                                        value: col.columnName,
                                    })) || []
                                }
                                labelClassName="selector-label-bold"
                                size="sm"
                            />
                            <ScoopSelector
                                label="Select the dataset with the lookup column"
                                value={selectedTargetInboxID}
                                onChange={(e) => setTargetInboxID(e.target.value)}
                                options={
                                    workspaceMetadata?.inboxes.map((inbox) => ({
                                        label: inbox.label,
                                        value: inbox.inboxID,
                                    })) || []
                                }
                                labelClassName="selector-label-bold"
                                size="sm"
                            />
                            {selectedTargetInboxID && (
                                <ScoopSelector
                                    label="Select a column to match in the target dataset"
                                    value={targetColumn}
                                    onChange={(e) => setTargetColumn(e.target.value)}
                                    options={
                                        selectedTargetInbox?.tables[0].columns.map((col) => ({
                                            label: col.columnName,
                                            value: col.columnName,
                                        })) || []
                                    }
                                    labelClassName="selector-label-bold"
                                    size="sm"
                                />
                            )}
                            {selectedTargetInboxID && (
                                <ScoopSelector
                                    label="Select a column with the value that you would like to lookup"
                                    value={targetValueColumn}
                                    onChange={(e) => {
                                        setTargetValueColumn(e.target.value)
                                        testValid(formulaName)
                                    }}
                                    options={
                                        selectedTargetInbox?.tables[0].columns.map((col) => ({
                                            label: col.columnName,
                                            value: col.columnName,
                                        })) || []
                                    }
                                    labelClassName="selector-label-bold"
                                    size="sm"
                                />
                            )}
                        </ScoopColumn>
                    </ScoopColumn>
                </ScoopDialog>
            )}
            {openAddSegment && (
                <ScoopDialog
                    className={styles.dialogLarge}
                    open={openAddSegment}
                    onClose={() => {
                        setOpenAddSegment(false)
                    }}
                    title={'Add a column that applies a segment/cluster model to this dataset'}
                    actions={
                        <ScoopRow className={styles.dialogActions}>
                            <ScoopButton
                                variant="grey"
                                onClick={() => {
                                    setOpenAddSegment(false)
                                }}
                            >
                                Cancel
                            </ScoopButton>
                            <ScoopButton
                                onClick={() => {
                                    createCalculatedField(
                                        formulaName,
                                        'ScoopApplyModel("' + selectedModel + '")'
                                    )
                                }}
                                disabled={
                                    formulaName.trim().length === 0 || selectedModel.length === 0
                                }
                            >
                                Create
                            </ScoopButton>
                        </ScoopRow>
                    }
                >
                    <ScoopColumn className={styles.dialogContent}>
                        <ScoopColumn>
                            <ScoopText className={styles.formLabel}>
                                Name of new column to create with the segment/cluster value
                            </ScoopText>
                            <ScoopTextField
                                value={formulaName}
                                onChange={(e) => {
                                    setFormulaName(e.target.value)
                                }}
                                placeholder="Enter new column name"
                            />
                        </ScoopColumn>
                        <ScoopColumn>
                            <ScoopSelector
                                label="Select the saved segment/cluster model to use"
                                value={selectedModel}
                                onChange={(e) => setSelectedModel(e.target.value)}
                                options={
                                    workspaceMetadata?.savedModels.map((model) => ({
                                        label: model.modelName,
                                        value: model.modelName,
                                    })) || []
                                }
                                labelClassName="selector-label-bold"
                                size="sm"
                            />
                        </ScoopColumn>
                    </ScoopColumn>
                </ScoopDialog>
            )}
        </ScoopColumn>
    )
}
